import type { AssessmentData } from '@apps/materiality-tracker/api/materiality-assessment';
import { EESGCategory, MaterialityBoundary, MaterialPillar } from '@apps/materiality-tracker/api/materiality-assessment';
import { OverviewActions } from './OverviewActions';
import { CategoriesCharts } from './CategoriesCharts';
import { DashboardSection } from '@g17eco/molecules/dashboard';
import { LockedSection } from '../locked-section/LockedSection';
import LockedInsightsImage from '../../../../images/assessment-lock/locked-insights.png';

// Mock data for locked insights preview
const MOCK_ASSESSMENT_DATA: AssessmentData[] = [
  // Environmental topics
  ...Array(45)
    .fill(null)
    .map((_, i) => ({
      code: `env-${i}`,
      name: `Environmental Topic ${i}`,
      score: 50,
      categories: {
        // Field name is 'esg' to match the data structure field (categories.esg),
        // but the category enum is EESGCategory which includes Economic, Environmental, Social, and Governance.
        // The 'esg' field name is kept for backward compatibility with existing data/API.
        esg: [EESGCategory.Environmental],
        materialPillar:
          i % 5 === 0 ? [MaterialPillar.Planet] : i % 5 === 1 ? [MaterialPillar.People] : [MaterialPillar.Prosperity],
        boundary:
          i % 7 === 0
            ? [MaterialityBoundary.Leadership]
            : i % 7 === 1
              ? [MaterialityBoundary.ResearchAndDevelopment]
              : i % 7 === 2
                ? [MaterialityBoundary.SupplyChain]
                : i % 7 === 3
                  ? [MaterialityBoundary.ProductAndServices]
                  : i % 7 === 4
                    ? [MaterialityBoundary.Distribution]
                    : i % 7 === 5
                      ? [MaterialityBoundary.Communities]
                      : [MaterialityBoundary.Experiences],
      },
    })),
  // Social topics
  ...Array(30)
    .fill(null)
    .map((_, i) => ({
      code: `soc-${i}`,
      name: `Social Topic ${i}`,
      score: 50,
      categories: {
        esg: [EESGCategory.Social],
        materialPillar:
          i % 5 === 0
            ? [MaterialPillar.People]
            : i % 5 === 1
              ? [MaterialPillar.Partnership]
              : [MaterialPillar.Principle],
        boundary:
          i % 7 === 0
            ? [MaterialityBoundary.Leadership]
            : i % 7 === 1
              ? [MaterialityBoundary.Communities]
              : i % 7 === 2
                ? [MaterialityBoundary.Experiences]
                : [MaterialityBoundary.ProductAndServices],
      },
    })),
  // Governance topics
  ...Array(25)
    .fill(null)
    .map((_, i) => ({
      code: `gov-${i}`,
      name: `Governance Topic ${i}`,
      score: 50,
      categories: {
        esg: [EESGCategory.Governance],
        materialPillar:
          i % 5 === 0
            ? [MaterialPillar.Principle]
            : i % 5 === 1
              ? [MaterialPillar.Prosperity]
              : [MaterialPillar.Partnership],
        boundary:
          i % 7 === 0
            ? [MaterialityBoundary.Leadership]
            : i % 7 === 1
              ? [MaterialityBoundary.ResearchAndDevelopment]
              : [MaterialityBoundary.SupplyChain],
      },
    })),
];

interface LockedInsightsProps {
  onUnlock: () => void;
}

export const LockedInsights = ({ onUnlock }: LockedInsightsProps) => {
  const features = [
    'Actionable insights from 400+ possible material topics',
    'Recommended metrics to report against, aligned with global standards',
    'Personalised materiality reports in xlsx, docx and pptx formats',
  ];

  return (
    <div>
      <OverviewActions isStaff={false} readOnly />
      <DashboardSection>
        <CategoriesCharts data={MOCK_ASSESSMENT_DATA} />
        <LockedSection
          title='Unlock topic scores and priority breakdown'
          features={features}
          buttonText='Unlock assessment results'
          onUnlock={onUnlock}
          backgroundImage={LockedInsightsImage}
        />
      </DashboardSection>
    </div>
  );
};
